<template>
  <div class="w-full h-full flex flex-col">
    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="searchQuery"
        placeholder="Search referrers..."
        @update:model-value="handleSearch"
        class="w-full"
      />
    </Teleport>
    <!-- Fixed Header Section -->
    <div class="flex-shrink-0 pb-2">
      <DashboardHeader title="Referrers">
        <template #right>
        <ReferrersFilters />

          <router-link
            to="/referrers/add-referrer"
            class="text-xs md:text-xs font-normal bg-cosmicPurple text-white px-2 pr-4 md:px-4 md:pl-2 py-2 rounded-md flex items-center hover:bg-cosmicPurple/80 transition-all duration-200 whitespace-nowrap"
          >
            <Plus class="w-4 h-4 mr-2" />
            Add new referrer
          </router-link>
        </template>
      </DashboardHeader>

      <ReferrersStats
        :total-referrers="totalReferrers"
        :total-companies="totalCompanies"
        :active-referrers="activeReferrers"
        :loading="referrersStore.isLoading"
      />
      <div class="mt-2">
      </div>
    </div>

    <!-- Scrollable Content Section -->
    <div class="flex-1 overflow-y-auto min-h-0">
      <!-- Loading State -->
      <div
        v-if="referrersStore.isLoading && !referrersStore.referrersGroupedByCompany.length"
        class="space-y-4 w-full p-4"
      >
        <div v-for="i in 3" :key="i" class="bg-white rounded-lg p-4 animate-pulse w-full">
          <div class="h-12 bg-gray-200 rounded-lg w-1/3"></div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="referrersStore.error"
        class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 w-full m-4"
      >
        {{ referrersStore.error }}
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!referrersStore.referrersGroupedByCompany.length"
        class="bg-white rounded-lg p-8 text-center w-full m-4"
      >
        <p class="text-gray-500">No referrers found</p>
      </div>

      <!-- Company List -->
      <div v-else class="space-y-4 w-full">
        <ReferrerCompanyCard
          v-for="company in referrersStore.referrersGroupedByCompany"
          :key="company.id"
          :company="company"
          :default-expanded="true"
          @show-company-details="showCompanyDetails"
          @show-referrer-details="showReferrerDetails"
        />
      </div>

      <!-- Infinite scroll sentinel -->
      <div ref="sentinelRef" class="flex justify-center items-center py-6">
        <div v-if="referrersStore.isLoadingMore" class="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 class="w-4 h-4 animate-spin" />
          Loading more...
        </div>
      </div>
    </div>

    <!-- Modals -->
    <CompanyDetailsModal v-model:is-open="isCompanyModalOpen" :company="selectedCompany" />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, onActivated, ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useReferrersStore } from '@/stores/referrers'
import { debounce } from 'lodash-es'
import ReferrerCompanyCard from '@/components/referrers/ReferrerCompanyCard.vue'
import CompanyDetailsModal from '@/components/referrers/CompanyDetailsModal.vue'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import { Plus, Loader2 } from 'lucide-vue-next'
import ReferrersStats from '@/components/referrers/ReferrersStats.vue'
import ReferrersFilters from '@/components/referrers/ReferrersFilters.vue'
import SearchInput from '@/components/common/SearchInput.vue'

const router = useRouter()
const route = useRoute()
const referrersStore = useReferrersStore()

// Modal states
const isCompanyModalOpen = ref(false)
const isReferrerModalOpen = ref(false)
const selectedCompany = ref(null)
const selectedReferrer = ref(null)
const searchQuery = ref('')

// Infinite scroll sentinel
const sentinelRef = ref(null)
let scrollObserver = null

// Stats come directly from the server response so they are accurate
// from the very first page and reflect the current filters.
const totalReferrers = computed(() => referrersStore.totalReferrersCount)
const totalCompanies = computed(() => referrersStore.totalCompaniesCount)
const activeReferrers = computed(() => referrersStore.activeReferrersCount)

const handleSearch = () => {
  referrersStore.setSearchQuery(searchQuery.value, router)
}

// Debounced server fetch triggered by search query changes
const debouncedFetch = debounce(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  referrersStore.fetchReferrersGroupedByCompany()
}, 300)

// Keep search query in sync with store and trigger debounced fetch
watch(
  () => referrersStore.searchQuery,
  () => {
    debouncedFetch()
  }
)

// Watch for route changes to refresh data when navigating to referrers page
watch(
  () => route.path,
  (newPath, oldPath) => {
    if (newPath === '/referrers' && oldPath !== '/referrers') {
      const isFromAddReferrer = oldPath === '/referrers/add-referrer'
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      referrersStore.fetchReferrersGroupedByCompany(isFromAddReferrer)
    }
  }
)

// Also refresh data when component is activated (for keep-alive scenarios)
onActivated(() => {
  const referrer = document.referrer
  const forceRefresh = referrer.includes('add-referrer') || referrer.includes('Add%20new%20referrer')
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  referrersStore.fetchReferrersGroupedByCompany(forceRefresh)
})

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  referrersStore.fetchReferrersGroupedByCompany()

  scrollObserver = new IntersectionObserver(
    (entries) => {
      if (
        entries[0].isIntersecting &&
        referrersStore.hasMore &&
        !referrersStore.isLoadingMore
      ) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        referrersStore.fetchNextPage()
      }
    },
    { threshold: 0.1 }
  )

  if (sentinelRef.value) {
    scrollObserver.observe(sentinelRef.value)
  }
})

onUnmounted(() => {
  scrollObserver?.disconnect()
})

// Show company details modal
const showCompanyDetails = (company) => {
  selectedCompany.value = company
  isCompanyModalOpen.value = true
}

// Show referrer details modal
const showReferrerDetails = (referrer) => {
  selectedReferrer.value = referrer
}
</script>
